import React, { useState, useMemo, useEffect } from 'react';
import axios from 'axios';
import { loadInsurance, loadProducts, loadProductDetails, addProduct, updateProductInsurances, removeProductInsurance, adjustStock, deleteProduct } from '../BaseURL';
import Swal from 'sweetalert2';
import {
  Box,
  Container,
  Paper,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Backdrop,
  CircularProgress,
  Grid,
  Divider,
  Stack,
  Badge,
  Alert
} from '@mui/material';
import {
  Inventory,
  LocalPharmacy,
  TrendingUp,
  Warning,
  Error,
  Search,
  FilterList,
  Refresh,
  Add,
  Edit,
  CheckCircle,
  Info,
  SwapHoriz,
  Store,
  LocalHospital,
  AddCircleOutline,
  RemoveCircleOutline,
  LockOutlined,
  DeleteOutline,
  Delete,
  DeleteSweep,
  Shield,
} from '@mui/icons-material';

const ProcurementHome = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Insurance state
  const [insuranceList, setInsuranceList] = useState([]);
  const [loadingInsurance, setLoadingInsurance] = useState(false);

  // Products state
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);

  // Dialog state
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [newProduct, setNewProduct] = useState({
    name: '',
    unitOfPricing: '',
    purchaseCost: '',
    doseUnit: '',
    productType: 'drug',
    code: '',
    reorderLevel: '',
    selectedInsurances: [] 
  });
  const [addingProduct, setAddingProduct] = useState(false);

  // Edit dialog state
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState({
    name: '',
    unitOfPricing: '',
    purchaseCost: '',
    doseUnit: '',
    productType: 'drug',
    code: '',
    reorderLevel: '',
    selectedInsurances: []
  });
  const [loadingProductDetails, setLoadingProductDetails] = useState(false);
  const [updatingProduct, setUpdatingProduct] = useState(false);

  // Product Movement Dialog state
  const [openMovementDialog, setOpenMovementDialog] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  // ── Stock Adjustment state ─────────────────────────────────────────────────
  const [adjustmentType, setAdjustmentType] = useState('add');       // 'add' | 'subtract'
  const [adjustmentTarget, setAdjustmentTarget] = useState('store'); // 'store' | 'dispensary'
  const [adjustmentQty, setAdjustmentQty] = useState('');
  const [adjustmentReason, setAdjustmentReason] = useState('');
  const [adjusting, setAdjusting] = useState(false);
  const [openPwdDialog, setOpenPwdDialog] = useState(false);
  const [pwdValue, setPwdValue] = useState('');
  const [pwdError, setPwdError] = useState('');
  const [pendingAdjustment, setPendingAdjustment] = useState(null);

  // ── Delete-product flow (admin + password) ─────────────────────────
  const [deleteTargetProduct, setDeleteTargetProduct] = useState(null);   // product object pending delete
  const [deletePwdOpen, setDeletePwdOpen]             = useState(false);  // password dialog
  const [deletePwdValue, setDeletePwdValue]           = useState('');
  const [deletePwdError, setDeletePwdError]           = useState('');
  const [deleting, setDeleting]                       = useState(false);

  // ── New Delete Dialog (per-insurance OR delete-all) ────────────────
  const [openDeleteDialog, setOpenDeleteDialog]       = useState(false);
  const [deleteDialogProduct, setDeleteDialogProduct] = useState(null);
  const [deleteDialogInsurances, setDeleteDialogInsurances] = useState([]); // [{id, name}]
  const [deleteDialogLoading, setDeleteDialogLoading] = useState(false);
  const [deleteMode, setDeleteMode]                   = useState('specific'); // 'specific' | 'all'
  const [deleteSelectedInsurance, setDeleteSelectedInsurance] = useState('');
  const [deleteAllPwd, setDeleteAllPwd]               = useState('');
  const [deleteAllPwdError, setDeleteAllPwdError]     = useState('');
  const [deleteSubmitting, setDeleteSubmitting]       = useState(false);

  // Load insurance data on component mount
  useEffect(() => {
    const fetchInsurance = async () => {
      setLoadingInsurance(true);
      try {
        const response = await axios.get(loadInsurance);
        if (response.data.success) {
          setInsuranceList(response.data.results);
        }
      } catch (error) {
        console.error('Error loading insurance:', error);
      } finally {
        setLoadingInsurance(false);
      }
    };

    fetchInsurance();
  }, []);

  // Load products data on component mount
  const fetchProducts = async () => {
    setLoadingProducts(true);
    try {
      const response = await axios.get(loadProducts);
      if (response.data.success) {
        setProducts(response.data.results);
      }
    } catch (error) {
      console.error('Error loading products:', error);
    } finally {
      setLoadingProducts(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  // Calculate statistics
  const stats = useMemo(() => {
    const totalStock = products.reduce((sum, p) => sum + parseInt(p.current_stock || 0), 0);
    const lowStockCount = products.filter(p => parseInt(p.current_stock || 0) <= parseInt(p.re_order || 0) && parseInt(p.current_stock || 0) > 0).length;
    const outOfStockCount = products.filter(p => parseInt(p.current_stock || 0) === 0).length;
    const inStockCount = products.filter(p => parseInt(p.current_stock || 0) > parseInt(p.re_order || 0)).length;

    return {
      totalProducts: products.length,
      totalStock: totalStock,
      lowStockCount: lowStockCount,
      outOfStockCount: outOfStockCount,
      inStockCount: inStockCount
    };
  }, [products]);

  // Filter products
  const filteredProducts = useMemo(() => {
    return products.filter(product => {
      const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesType = typeFilter === 'all' || 
        (product.types && product.types.toLowerCase().includes(typeFilter.toLowerCase()));

      const currentStock = parseInt(product.current_stock || 0);
      const reorderLevel = parseInt(product.re_order || 0);
      let productStatus = 'normal';
      if (currentStock <= 0) {
        productStatus = 'out';
      } else if (currentStock <= reorderLevel) {
        productStatus = 'low';
      }

      const matchesStatus = statusFilter === 'all' || productStatus === statusFilter;

      return matchesSearch && matchesType && matchesStatus;
    });
  }, [products, searchTerm, typeFilter, statusFilter]);

  // Reset page when filters change
  useEffect(() => {
    setPage(0);
  }, [searchTerm, typeFilter, statusFilter]);

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'normal': return 'success';
      case 'low': return 'warning';
      case 'out': return 'error';
      default: return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'normal': return 'In Stock';
      case 'low': return 'Low Stock';
      case 'out': return 'Out of Stock';
      default: return status;
    }
  };

  // Dialog handlers
  const handleOpenAddDialog = () => {
    setOpenAddDialog(true);
  };

  const handleCloseAddDialog = () => {
    setOpenAddDialog(false);
    setNewProduct({
      name: '',
      unitOfPricing: '',
      purchaseCost: '',
      doseUnit: '',
      productType: 'drug',
      code: '',
      reorderLevel: '',
      selectedInsurances: []
    });
  };

  const handleInputChange = (field, value) => {
    setNewProduct(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleInsuranceToggle = (insuranceId) => {
    setNewProduct(prev => ({
      ...prev,
      selectedInsurances: prev.selectedInsurances.includes(insuranceId)
        ? prev.selectedInsurances.filter(id => id !== insuranceId)
        : [...prev.selectedInsurances, insuranceId]
    }));
  };

  const handleAddProduct = async () => {
    // Validation
    if (!newProduct.name || !newProduct.unitOfPricing || !newProduct.purchaseCost || 
        !newProduct.doseUnit || !newProduct.productType || !newProduct.reorderLevel) {
      Swal.fire({
        icon: 'error',
        title: 'Validation Error',
        text: 'Please fill in all required fields'
      });
      return;
    }

    if (newProduct.selectedInsurances.length === 0) {
      Swal.fire({
        icon: 'error',
        title: 'Validation Error',
        text: 'Please select at least one insurance'
      });
      return;
    }

    // Confirmation dialog
    const result = await Swal.fire({
      title: 'Add Product',
      text: 'Are you sure you want to add this product?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, add it!'
    });

    if (!result.isConfirmed) {
      return;
    }

   setAddingProduct(true);
    
    try {
      const response = await axios.post(addProduct, {
        name: newProduct.name,
        unitOfPricing: newProduct.unitOfPricing,
        purchaseCost: newProduct.purchaseCost,
        doseUnit: newProduct.doseUnit,
        productType: newProduct.productType,
        code: newProduct.code,
        reorderLevel: newProduct.reorderLevel,
        selectedInsurances: newProduct.selectedInsurances
      });

      if (response.data.success) {
      Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: response.data.message,
        timer: 2000,
        showConfirmButton: false
      });

      setNewProduct({
        name: '',
        unitOfPricing: '',
        purchaseCost: '',
        doseUnit: '',
        productType: 'drug',
        code: '',
        reorderLevel: '',
        selectedInsurances: []
      });

      fetchProducts();
    }
 else {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: response.data.message || 'Error adding product'
        });
      }
    } catch (error) {
      console.error('Error adding product:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Error adding product. Please try again.'
      });
    } finally {
      setAddingProduct(false);
    }
  };

  // Edit dialog handlers
  const handleEditProduct = async (product) => {
    setLoadingProductDetails(true);
    setOpenEditDialog(true);

    try {
      let productName=product.name
      const response = await axios.get(loadProductDetails, {
              params: {
                productName
              }
            });

      if (response.data.success) {
        const productData = response.data.product;
        
        setEditingProduct({
          name: productData?.name || '',
          unitOfPricing: productData?.pricing || '',
          purchaseCost: parseFloat(productData?.cost || 0),
          doseUnit: productData?.Dose_Unit || '',
          productType: productData?.type || 'drug',
          productRoute: productData?.unin || '',
          code: productData?.code || '',
          reorderLevel: parseInt(productData?.re_order || 0),
          selectedInsurances: response.data.associatedInsurances || []
        });
      }
    } catch (error) {
      console.error('Error loading product details:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Error loading product details'
      });
    } finally {
      setLoadingProductDetails(false);
    }
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setEditingProduct({
      name: '',
      unitOfPricing: '',
      purchaseCost: '',
      doseUnit: '',
      productType: 'drug',
      code: '',
      reorderLevel: '',
      selectedInsurances: []
    });
  };

  const handleEditInputChange = (field, value) => {
    setEditingProduct(prev => ({
      ...prev,
      [field]: value
    }));
  };


  const handleEditInsuranceToggle = async (insuranceId, insuranceName) => {
  const alreadyChecked = editingProduct.selectedInsurances.includes(insuranceId);

  if (alreadyChecked) {
    const confirm = await Swal.fire({
      title: 'Remove Insurance?',
      text: `Are you sure you want to delete this product from ${insuranceName}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, remove',
      confirmButtonColor: '#d33'
    });

    if (!confirm.isConfirmed) return;

    try {
      setUpdatingProduct(true);

      await axios.post(removeProductInsurance, {
        name: editingProduct.name,
        insuranceId
      });

      setEditingProduct(prev => ({
        ...prev,
        selectedInsurances: prev.selectedInsurances.filter(id => id !== insuranceId)
      }));

      Swal.fire('Removed!', 'Insurance removed successfully', 'success');

    } catch (err) {
      Swal.fire('Error', 'Failed to remove insurance', 'error');
    } finally {
      setUpdatingProduct(false);
    }

    return;
  }

  const confirmAdd = await Swal.fire({
    title: 'Add Insurance?',
    text: `Do you want to add this product to ${insuranceName}?`,
    icon: 'question',
    showCancelButton: true,
    confirmButtonText: 'Yes, add',
    confirmButtonColor: '#10b981'
  });

  if (!confirmAdd.isConfirmed) return;

  try {
    setUpdatingProduct(true);

    await axios.post(addProduct, {
      ...editingProduct,
      selectedInsurances: [insuranceId]
    });

    setEditingProduct(prev => ({
      ...prev,
      selectedInsurances: [...prev.selectedInsurances, insuranceId]
    }));

    Swal.fire('Added!', 'Insurance added successfully', 'success');

  } catch (err) {
    Swal.fire('Error', 'Failed to add insurance', 'error');
  } finally {
    setUpdatingProduct(false);
  }
};



  const handleUpdateProduct = async () => {
    // Truthiness checks used to reject reorderLevel=0 and purchaseCost=0
    // outright, which is why some products couldn't be updated at all —
    // loadProductDetails coerces missing values to 0. Distinguish "empty
    // string / null" (missing) from "0" (valid numeric input).
    const missing = [];
    const isBlank = (v) => v === '' || v === null || v === undefined;
    if (isBlank(editingProduct.name))          missing.push('name');
    if (isBlank(editingProduct.unitOfPricing)) missing.push('unit of pricing');
    if (isBlank(editingProduct.doseUnit))      missing.push('dose unit');
    if (isBlank(editingProduct.productType))   missing.push('product type');
    if (isBlank(editingProduct.purchaseCost) || isNaN(Number(editingProduct.purchaseCost))) missing.push('purchase cost');
    if (isBlank(editingProduct.reorderLevel)  || isNaN(Number(editingProduct.reorderLevel)))  missing.push('reorder level');
    if (missing.length) {
      Swal.fire({
        icon: 'error',
        title: 'Validation Error',
        text: `Please fill in: ${missing.join(', ')}`
      });
      return;
    }

    if (editingProduct.selectedInsurances.length === 0) {
      Swal.fire({
        icon: 'error',
        title: 'Validation Error',
        text: 'Please select at least one insurance'
      });
      return;
    }

    const result = await Swal.fire({
      title: 'Update Product',
      text: 'Are you sure you want to update this product?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, update it!'
    });

    if (!result.isConfirmed) {
      return;
    }

    setUpdatingProduct(true);

    try {
      const response = await axios.put(updateProductInsurances, {
        productName:editingProduct.name,
        name: editingProduct.name,
        unitOfPricing: editingProduct.unitOfPricing,
        purchaseCost: editingProduct.purchaseCost,
        doseUnit: editingProduct.doseUnit,
        productType: editingProduct.productType,
        code: editingProduct.code,
        reorderLevel: editingProduct.reorderLevel,
        selectedInsurances: editingProduct.selectedInsurances
      });

      if (response.data.success) {
        Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: response.data.message,
          timer: 2000,
          showConfirmButton: false
        });
        handleCloseEditDialog();
        fetchProducts();
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Update Failed',
          text: response.data.message || 'Failed to update product'
        });
      }
    } catch (error) {
      console.error('Error updating product:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.message || 'Error updating product'
      });
    } finally {
      setUpdatingProduct(false);
    }
  };

  // ── Stock Movement Dialog handlers ─────────────────────────────────────────
 const handleOpenMovementDialog = async (product) => {
  const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
  if (userInfo.role !== 'System Administrator') {
    Swal.fire({
      title: 'Access Denied',
      html: `<div style="text-align:center;padding:8px 0">
        <p style="color:#374151;margin-bottom:8px">Only a <strong>System Administrator</strong> can adjust stock quantities.</p>
        <p style="color:#9ca3af;font-size:0.85rem;margin:0">Your role: <strong style="color:#ef4444">${userInfo.role || 'Unknown'}</strong></p>
      </div>`,
      icon: 'error',
      confirmButtonColor: '#41a386',
    });
    return;
  }

  // ── Show dialog immediately with current data ──
  setSelectedProduct(product);
  setAdjustmentType('add');
  setAdjustmentTarget('store');
  setAdjustmentQty('');
  setAdjustmentReason('');
  setOpenMovementDialog(true);

  // ── Then silently refresh to get live stock ──
  try {
    const res = await axios.get(loadProducts);
    if (res.data.success) {
      const fresh = res.data.results.find(p => p.name === product.name);
      if (fresh) {
        setSelectedProduct(fresh); // update dialog with fresh numbers
        setProducts(res.data.results); // update table too
      }
    }
  } catch (err) {
    console.warn('Could not refresh stock:', err);
  }
};

  const handleCloseMovementDialog = () => {
    setOpenMovementDialog(false);
    setSelectedProduct(null);
  };

  // Step 1: validate then open MUI password dialog
  const handleAdjustStock = () => {
    if (!adjustmentQty || parseInt(adjustmentQty) <= 0) {
      Swal.fire({ icon: 'error', title: 'Invalid Quantity', text: 'Please enter a valid quantity greater than 0.', toast: true, position: 'top-end', timer: 3000, showConfirmButton: false });
      return;
    }
    if (!adjustmentReason.trim()) {
      Swal.fire({ icon: 'error', title: 'Reason Required', text: 'Please enter a reason for this adjustment.', toast: true, position: 'top-end', timer: 3000, showConfirmButton: false });
      return;
    }
    if (adjustmentType === 'subtract') {
      const currentQty = adjustmentTarget === 'store'
        ? parseInt(selectedProduct.inventory_stock || 0)
        : parseInt(selectedProduct.dispensary_stock || 0);
      if (parseInt(adjustmentQty) > currentQty) {
        Swal.fire({ icon: 'error', title: 'Insufficient Stock', text: `Cannot remove ${adjustmentQty} units — only ${currentQty} available in ${adjustmentTarget}.` });
        return;
      }
    }
    setPwdValue('');
    setPwdError('');
    setOpenPwdDialog(true);
  };

  // Step 2: submit with password from MUI dialog
  const handleConfirmAdjustment = async () => {
    if (!pwdValue.trim()) { setPwdError('Password cannot be empty'); return; }
    const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
    const username = userInfo.username || '';
    setAdjusting(true);
    try {
      const res = await axios.post(adjustStock, {
        productName: selectedProduct.name,
        type: adjustmentType,
        target: adjustmentTarget,
        quantity: parseInt(adjustmentQty),
        reason: adjustmentReason.trim(),
        username,
        password: pwdValue,
      });
      if (res.data.success) {
        setOpenPwdDialog(false);
        Swal.fire({ icon: 'success', title: 'Adjusted!', text: res.data.message || 'Stock updated successfully.', timer: 2000, showConfirmButton: false });
        handleCloseMovementDialog();
        fetchProducts();
      } else {
        throw new Error(res.data.message || 'Adjustment failed');
      }
    } catch (err) {
      if (err.response?.status === 401) {
        setPwdError('Wrong password. Please try again.');
      } else {
        setOpenPwdDialog(false);
        Swal.fire({ title: 'Error!', text: err.response?.data?.message || err.message || 'Something went wrong.', icon: 'error', confirmButtonColor: '#41a386' });
      }
    } finally {
      setAdjusting(false);
    }
  };

  // ── Delete-product flow ─────────────────────────────────────────────
  // Step 1: row click → role check + confirmation. Only the System
  // Administrator role can even open the password prompt.
  const handleRequestDeleteProduct = async (product) => {
    const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
    if (userInfo.role !== 'System Administrator') {
      Swal.fire({
        title: 'Access Denied',
        html: `<div style="text-align:center;padding:8px 0">
          <p style="color:#374151;margin-bottom:8px">Only a <strong>System Administrator</strong> can delete products from the store.</p>
          <p style="color:#9ca3af;font-size:0.85rem;margin:0">Your role: <strong style="color:#ef4444">${userInfo.role || 'Unknown'}</strong></p>
        </div>`,
        icon: 'error',
        confirmButtonColor: '#41a386',
      });
      return;
    }

    const totalStock = parseInt(product.current_stock || 0);
    const escapeHtml = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');

    const confirm = await Swal.fire({
      title: 'Delete Product?',
      html: `<div style="text-align:left;font-family:Inter,Segoe UI,sans-serif;">
        <div style="display:flex;align-items:center;gap:12px;padding:12px;border:1px solid #e5e9ef;border-radius:10px;background:#f8fafc;">
          <div style="flex:1;min-width:0;">
            <div style="font-weight:700;color:#0f172a;font-size:1rem;line-height:1.2;">${escapeHtml(product.name)}</div>
            <div style="color:#64748b;font-size:0.78rem;margin-top:2px;">${product.code ? 'Code: ' + escapeHtml(product.code) + ' · ' : ''}${escapeHtml(product.dose_units || '')}</div>
          </div>
        </div>
        <div style="margin-top:10px;padding:8px 12px;border-radius:8px;background:#FEE2E2;color:#991B1B;font-weight:600;font-size:0.85rem;">
          This permanently removes the product from <strong>store_pro</strong>, <strong>inventory_bal</strong>, and <strong>dispensary_bal</strong>.
        </div>
        ${totalStock > 0 ? `
          <div style="margin-top:8px;padding:8px 12px;border-radius:8px;background:#FEF3C7;color:#92400E;font-weight:600;font-size:0.85rem;">
            ⚠ Stock is still <strong>${totalStock}</strong> unit(s). Adjust stock to zero first or the server will reject the delete.
          </div>` : ''}
      </div>`,
      icon: undefined,
      showCancelButton: true,
      confirmButtonText: 'Continue',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#64748b',
      width: 520,
      focusCancel: true,
    });
    if (!confirm.isConfirmed) return;

    setDeleteTargetProduct(product);
    setDeletePwdValue('');
    setDeletePwdError('');
    setDeletePwdOpen(true);
  };

  // Step 2: password dialog → POST /deleteProduct.
  const handleConfirmDeleteProduct = async () => {
    if (!deletePwdValue.trim()) { setDeletePwdError('Password cannot be empty'); return; }
    const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
    const username = userInfo.username || '';
    setDeleting(true);
    try {
      const res = await axios.post(deleteProduct, {
        productName: deleteTargetProduct.name,
        username,
        password: deletePwdValue,
      });
      if (res.data.success) {
        setDeletePwdOpen(false);
        setDeleteTargetProduct(null);
        Swal.fire({
          icon: 'success',
          title: 'Deleted',
          text: res.data.message || 'Product removed from the store.',
          timer: 2000, showConfirmButton: false,
        });
        fetchProducts();
      } else {
        throw new Error(res.data.message || 'Delete failed');
      }
    } catch (err) {
      if (err.response?.status === 401) {
        setDeletePwdError('Wrong password. Please try again.');
      } else if (err.response?.status === 403) {
        setDeletePwdOpen(false);
        Swal.fire('Access Denied', err.response?.data?.message || 'Only a System Administrator can delete products.', 'error');
      } else if (err.response?.status === 409) {
        setDeletePwdOpen(false);
        Swal.fire('Cannot Delete', err.response?.data?.message || 'Server refused the delete.', 'warning');
      } else {
        setDeletePwdOpen(false);
        Swal.fire('Error', err.response?.data?.message || err.message || 'Something went wrong.', 'error');
      }
    } finally {
      setDeleting(false);
    }
  };

  // ── New Delete Dialog handlers ─────────────────────────────────────
  const handleOpenDeleteDialog = async (product) => {
    const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
    if (userInfo.role !== 'System Administrator') {
      Swal.fire({
        title: 'Access Denied',
        html: `<div style="text-align:center;padding:8px 0">
          <p style="color:#374151;margin-bottom:8px">Only a <strong>System Administrator</strong> can delete products.</p>
          <p style="color:#9ca3af;font-size:0.85rem;margin:0">Your role: <strong style="color:#ef4444">${userInfo.role || 'Unknown'}</strong></p>
        </div>`,
        icon: 'error',
        confirmButtonColor: '#41a386',
      });
      return;
    }

    setDeleteDialogProduct(product);
    setDeleteMode('specific');
    setDeleteSelectedInsurance('');
    setDeleteAllPwd('');
    setDeleteAllPwdError('');
    setDeleteDialogInsurances([]);
    setOpenDeleteDialog(true);
    setDeleteDialogLoading(true);

    try {
      const resp = await axios.get(loadProductDetails, { params: { productName: product.name } });
      if (resp.data.success) {
        const list = resp.data.insuranceList || [];
        const associatedIds = resp.data.associatedInsurances || [];
        const associated = list.filter((i) => associatedIds.includes(i.id));
        setDeleteDialogInsurances(associated);
        if (associated.length === 1) {
          setDeleteSelectedInsurance(String(associated[0].id));
        }
      }
    } catch (e) {
      console.error('Error loading product insurances:', e);
      Swal.fire('Error', 'Could not load insurance associations for this product.', 'error');
    } finally {
      setDeleteDialogLoading(false);
    }
  };

  const handleCloseDeleteDialog = () => {
    if (deleteSubmitting) return;
    setOpenDeleteDialog(false);
    setDeleteDialogProduct(null);
    setDeleteDialogInsurances([]);
    setDeleteMode('specific');
    setDeleteSelectedInsurance('');
    setDeleteAllPwd('');
    setDeleteAllPwdError('');
  };

  const handleSubmitDelete = async () => {
    if (!deleteDialogProduct) return;

    // Per-insurance removal
    if (deleteMode === 'specific') {
      if (!deleteSelectedInsurance) {
        Swal.fire({
          icon: 'warning',
          title: 'Pick an Insurance',
          text: 'Select the insurance to remove this product from.',
          toast: true, position: 'top-end', timer: 2500, showConfirmButton: false,
        });
        return;
      }
      const ins = deleteDialogInsurances.find((i) => String(i.id) === String(deleteSelectedInsurance));
      const confirm = await Swal.fire({
        title: 'Remove from insurance?',
        html: `<div style="text-align:left">
          Remove <strong>${deleteDialogProduct.name}</strong> from <strong>${ins?.name || ''}</strong>?
          <div style="margin-top:8px;color:#64748b;font-size:0.85rem;">
            The product stays available under other insurance schemes and keeps its inventory.
          </div>
        </div>`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, remove',
        cancelButtonText: 'Cancel',
        confirmButtonColor: '#dc2626',
        focusCancel: true,
      });
      if (!confirm.isConfirmed) return;

      setDeleteSubmitting(true);
      try {
        const res = await axios.post(removeProductInsurance, {
          name: deleteDialogProduct.name,
          insuranceId: parseInt(deleteSelectedInsurance, 10),
        });
        if (res.data.success) {
          Swal.fire({
            icon: 'success', title: 'Removed',
            text: res.data.message || `Removed from ${ins?.name || 'insurance'}.`,
            timer: 2000, showConfirmButton: false,
          });
          handleCloseDeleteDialog();
          fetchProducts();
        } else {
          throw new Error(res.data.message || 'Remove failed');
        }
      } catch (err) {
        Swal.fire('Error', err.response?.data?.message || err.message || 'Could not remove insurance.', 'error');
      } finally {
        setDeleteSubmitting(false);
      }
      return;
    }

    // Delete-all (force) — wipes the product from store_pro + inventory_bal + dispensary_bal
    if (!deleteAllPwd.trim()) { setDeleteAllPwdError('Password is required to delete all.'); return; }
    const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
    const username = userInfo.username || '';

    setDeleteSubmitting(true);
    try {
      const res = await axios.post(deleteProduct, {
        productName: deleteDialogProduct.name,
        username,
        password: deleteAllPwd,
        force: true,
      });
      if (res.data.success) {
        Swal.fire({
          icon: 'success', title: 'Deleted',
          text: res.data.message || 'Product fully removed from the system.',
          timer: 2200, showConfirmButton: false,
        });
        handleCloseDeleteDialog();
        fetchProducts();
      } else {
        throw new Error(res.data.message || 'Delete failed');
      }
    } catch (err) {
      if (err.response?.status === 401) {
        setDeleteAllPwdError('Wrong password. Please try again.');
      } else if (err.response?.status === 403) {
        Swal.fire('Access Denied', err.response?.data?.message || 'Only a System Administrator can delete products.', 'error');
      } else {
        Swal.fire('Error', err.response?.data?.message || err.message || 'Could not delete the product.', 'error');
      }
    } finally {
      setDeleteSubmitting(false);
    }
  };

  const getStockStatus = (stock, reorder) => {
    const s = parseInt(stock || 0);
    const r = parseInt(reorder || 0);
    if (s <= 0) return { color: '#ef4444', status: 'out', label: 'Out of Stock' };
    if (s <= r) return { color: '#f97316', status: 'low', label: 'Low Stock' };
    return { color: '#10b981', status: 'normal', label: 'In Stock' };
  };

  // ── Theme tokens (kept local so the rest of the file is unchanged) ──
  const PRIMARY      = '#41a386';
  const PRIMARY_DARK = '#2f8068';
  const PRIMARY_BG   = 'rgba(65,163,134,0.10)';
  const BORDER       = '#e5e9ef';
  const MUTED        = '#64748b';
  const STRONG       = '#0f172a';
  const LABEL_SX = {
    fontSize: '0.66rem', fontWeight: 700, color: '#94a3b8',
    textTransform: 'uppercase', letterSpacing: '0.6px',
  };

  return (
    <Box sx={{
      flexGrow: 1,
      bgcolor: '#f8fafc',
      minHeight: '100vh',
      py: 2.5,
    }}>
      <Container maxWidth="xl">
        {/* ── Header ────────────────────────────────────────────────── */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={1.5}
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography sx={{ ...LABEL_SX, color: PRIMARY, mb: 0.25 }}>
              Procurement · Store
            </Typography>
            <Typography sx={{ fontSize: '1.4rem', fontWeight: 700, color: STRONG, lineHeight: 1.2 }}>
              Inventory & Products
            </Typography>
            <Typography sx={{ fontSize: '0.78rem', color: MUTED, mt: 0.25 }}>
              {stats.totalProducts} item{stats.totalProducts === 1 ? '' : 's'} on file ·
              {' '}{filteredProducts.length} matching current filters
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Tooltip title="Refresh data" arrow>
              <IconButton
                onClick={fetchProducts}
                size="small"
                sx={{
                  border: `1px solid ${BORDER}`, bgcolor: '#fff', color: MUTED,
                  '&:hover': { bgcolor: '#f1f5f9', color: STRONG },
                }}
              >
                <Refresh fontSize="small" />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleOpenAddDialog}
              sx={{
                bgcolor: PRIMARY,
                '&:hover': { bgcolor: PRIMARY_DARK },
                textTransform: 'none', fontWeight: 700, px: 2.5,
              }}
            >
              Add Product
            </Button>
          </Stack>
        </Stack>

        {/* ── Stats strip (4 compact tiles in a single row) ─────────── */}
        <Grid container spacing={1.25} sx={{ mb: 2 }}>
          {[
            { label: 'Total Products', value: stats.totalProducts,    icon: <Inventory  fontSize="small" />, tint: '#f1f5f9',                color: STRONG    },
            { label: 'In Stock',       value: stats.inStockCount,     icon: <CheckCircle fontSize="small" />, tint: 'rgba(16,185,129,0.10)', color: '#15803D' },
            { label: 'Low Stock',      value: stats.lowStockCount,    icon: <Warning    fontSize="small" />, tint: 'rgba(217,119,6,0.10)',  color: '#A16207' },
            { label: 'Out of Stock',   value: stats.outOfStockCount,  icon: <Error      fontSize="small" />, tint: 'rgba(220,38,38,0.10)',  color: '#991B1B' },
          ].map(t => (
            <Grid item xs={6} md={3} key={t.label}>
              <Paper elevation={0} sx={{
                p: 1.5, borderRadius: 2,
                border: `1px solid ${BORDER}`, bgcolor: '#fff',
                display: 'flex', alignItems: 'center', gap: 1.5,
              }}>
                <Box sx={{
                  width: 36, height: 36, borderRadius: 1.5,
                  bgcolor: t.tint, color: t.color,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  {t.icon}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={LABEL_SX}>{t.label}</Typography>
                  <Typography sx={{ fontSize: '1.35rem', fontWeight: 800, color: STRONG, lineHeight: 1.1, mt: 0.25 }}>
                    {Number(t.value || 0).toLocaleString()}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* ── Products Card ─────────────────────────────────────────── */}
        <Paper elevation={0} sx={{
          border: `1px solid ${BORDER}`, borderRadius: 2, overflow: 'hidden', bgcolor: '#fff',
        }}>
          {/* Filter row — compact, single line on desktop */}
          <Box sx={{
            p: 1.5,
            borderBottom: `1px solid ${BORDER}`,
            display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1,
            bgcolor: '#fff',
          }}>
            <TextField
              size="small"
              placeholder="Search by product name…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ minWidth: 240, flex: { xs: '1 1 100%', md: '0 0 320px' } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" sx={{ color: '#94a3b8' }} />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Type</InputLabel>
              <Select
                label="Type"
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
              >
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value="drug">Drugs</MenuItem>
                {/* Consumables removed from this dropdown — they now live in
                    the dedicated Procurement → Consumables menu entry. */}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Stock Status</InputLabel>
              <Select
                label="Stock Status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="normal">In Stock</MenuItem>
                <MenuItem value="low">Low Stock</MenuItem>
                <MenuItem value="out">Out of Stock</MenuItem>
              </Select>
            </FormControl>
            <Box sx={{ flex: 1 }} />
            <Typography sx={{ fontSize: '0.74rem', color: MUTED }}>
              {filteredProducts.length} of {products.length} shown
            </Typography>
          </Box>

          {/* Table */}
          <TableContainer sx={{ maxHeight: '62vh' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow sx={{ '& th': { bgcolor: '#f8fafc', borderBottom: `1px solid ${BORDER}`, ...LABEL_SX, py: 1.25 } }}>
                  <TableCell sx={{ minWidth: 220 }}>Product</TableCell>
                  <TableCell align="center" sx={{ width: 110 }}>Type</TableCell>
                  <TableCell align="center" sx={{ width: 110 }}>Dose unit</TableCell>
                  <TableCell align="right" sx={{ width: 100 }}>Unit cost</TableCell>
                  <TableCell align="center" sx={{ width: 90 }}>Total</TableCell>
                  <TableCell align="center" sx={{ width: 90 }}>Store</TableCell>
                  <TableCell align="center" sx={{ width: 100 }}>Dispensary</TableCell>
                  <TableCell align="center" sx={{ width: 70 }}>Reorder</TableCell>
                  <TableCell align="center" sx={{ width: 110 }}>Status</TableCell>
                  <TableCell align="right" sx={{ width: 90 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredProducts
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((product, index) => {
                    const stockInfo = getStockStatus(product.current_stock, product.re_order);
                    const inventoryStock = parseInt(product.inventory_stock || 0);
                    const dispensaryStock = parseInt(product.dispensary_stock || 0);
                    const totalStock = parseInt(product.current_stock || 0);
                    const isDrug = product.types === 'drug';
                    const statusTone = stockInfo.status === 'normal'
                      ? { bg: 'rgba(16,185,129,0.10)', color: '#15803D' }
                      : stockInfo.status === 'low'
                        ? { bg: 'rgba(217,119,6,0.10)', color: '#A16207' }
                        : { bg: 'rgba(220,38,38,0.10)', color: '#991B1B' };

                    return (
                      <TableRow
                        key={product.id || product.name || index}
                        sx={{
                          '&:hover': { bgcolor: '#f8fafc' },
                          borderBottom: `1px solid ${BORDER}`,
                        }}
                      >
                        <TableCell>
                          <Typography sx={{ fontSize: '0.86rem', fontWeight: 700, color: STRONG, lineHeight: 1.25 }}>
                            {product.name}
                          </Typography>
                          {product.code && (
                            <Typography sx={{ fontSize: '0.7rem', color: MUTED, mt: 0.25 }}>
                              Code: {product.code}
                            </Typography>
                          )}
                          {Number(product.insurance_count || 0) === 0 && (
                            <Box sx={{
                              display: 'inline-flex', alignItems: 'center', gap: 0.5,
                              mt: 0.5, px: 0.75, py: 0.15, borderRadius: 0.75,
                              bgcolor: 'rgba(220,38,38,0.08)', color: '#991B1B',
                              fontSize: '0.62rem', fontWeight: 700,
                              textTransform: 'uppercase', letterSpacing: '0.4px',
                            }}>
                              <Warning sx={{ fontSize: 11 }} />
                              No insurance attached
                            </Box>
                          )}
                        </TableCell>

                        <TableCell align="center">
                          <Chip
                            icon={isDrug ? <LocalPharmacy sx={{ fontSize: 14 }} /> : <Inventory sx={{ fontSize: 14 }} />}
                            label={product.types || '—'}
                            size="small"
                            sx={{
                              height: 22, fontSize: '0.7rem', fontWeight: 700,
                              bgcolor: isDrug ? 'rgba(14,165,233,0.10)' : 'rgba(245,158,11,0.10)',
                              color:   isDrug ? '#0369A1' : '#92400E',
                              '& .MuiChip-icon': { color: 'inherit' },
                            }}
                          />
                        </TableCell>

                        <TableCell align="center">
                          <Typography sx={{ fontSize: '0.78rem', color: '#475569' }}>
                            {product.dose_units || '—'}
                          </Typography>
                        </TableCell>

                        <TableCell align="right">
                          <Typography sx={{ fontSize: '0.84rem', fontWeight: 700, color: STRONG, fontVariantNumeric: 'tabular-nums' }}>
                            ₵{parseFloat(product.cost || 0).toFixed(2)}
                          </Typography>
                        </TableCell>

                        <TableCell align="center">
                          <Typography sx={{
                            fontSize: '0.92rem', fontWeight: 800,
                            color: stockInfo.color,
                            fontVariantNumeric: 'tabular-nums',
                          }}>
                            {totalStock.toLocaleString()}
                          </Typography>
                        </TableCell>

                        <TableCell align="center">
                          <Typography sx={{ fontSize: '0.84rem', fontWeight: 600, color: '#0F172A', fontVariantNumeric: 'tabular-nums' }}>
                            {inventoryStock.toLocaleString()}
                          </Typography>
                        </TableCell>

                        <TableCell align="center">
                          <Typography sx={{ fontSize: '0.84rem', fontWeight: 600, color: '#0F172A', fontVariantNumeric: 'tabular-nums' }}>
                            {dispensaryStock.toLocaleString()}
                          </Typography>
                        </TableCell>

                        <TableCell align="center">
                          <Typography sx={{ fontSize: '0.78rem', color: MUTED, fontVariantNumeric: 'tabular-nums' }}>
                            {parseInt(product.re_order || 0)}
                          </Typography>
                        </TableCell>

                        <TableCell align="center">
                          <Box sx={{
                            display: 'inline-flex', alignItems: 'center', gap: 0.5,
                            px: 1, py: 0.25, borderRadius: 1,
                            bgcolor: statusTone.bg, color: statusTone.color,
                            fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px',
                          }}>
                            {stockInfo.status === 'normal' ? <CheckCircle sx={{ fontSize: 12 }} /> :
                             stockInfo.status === 'low'    ? <Warning    sx={{ fontSize: 12 }} /> :
                                                              <Error      sx={{ fontSize: 12 }} />}
                            {stockInfo.label}
                          </Box>
                        </TableCell>

                        <TableCell align="right">
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                            <Tooltip title="Edit Product" arrow>
                              <IconButton
                                size="small"
                                onClick={() => handleEditProduct(product)}
                                sx={{ color: PRIMARY, '&:hover': { bgcolor: PRIMARY_BG } }}
                              >
                                <Edit sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Adjust Stock (Admin)" arrow>
                              <IconButton
                                size="small"
                                onClick={() => handleOpenMovementDialog(product)}
                                sx={{ color: '#8b5cf6', '&:hover': { bgcolor: 'rgba(139,92,246,0.10)' } }}
                              >
                                <SwapHoriz sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete (per-insurance or wipe entirely)" arrow>
                              <IconButton
                                size="small"
                                onClick={() => handleOpenDeleteDialog(product)}
                                sx={{ color: '#dc2626', '&:hover': { bgcolor: 'rgba(220,38,38,0.10)' } }}
                              >
                                <DeleteOutline sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={filteredProducts.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
            sx={{
              borderTop: `1px solid ${BORDER}`,
              '.MuiTablePagination-toolbar': { minHeight: 44 },
              '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                fontSize: '0.78rem', color: MUTED,
              },
              '.MuiTablePagination-select': { fontSize: '0.78rem', fontWeight: 700, color: STRONG },
            }}
          />

          {!loadingProducts && filteredProducts.length === 0 && (
            <Box sx={{ p: 5, textAlign: 'center', borderTop: `1px solid ${BORDER}` }}>
              <Inventory sx={{ fontSize: 36, color: '#cbd5e1', mb: 1 }} />
              <Typography sx={{ fontSize: '0.92rem', fontWeight: 700, color: STRONG }}>
                No products found
              </Typography>
              <Typography sx={{ fontSize: '0.78rem', color: MUTED, mt: 0.25 }}>
                Try adjusting your search or filter criteria
              </Typography>
            </Box>
          )}
        </Paper>
      </Container>

      {/* ── Add Product Dialog (unchanged) ──────────────────────────────────── */}
      {/* ── Add New Product Dialog ───────────────────────────────────── */}
      <Dialog open={openAddDialog} onClose={handleCloseAddDialog} maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{
          display: 'flex', alignItems: 'center', gap: 1.5, py: 2,
          bgcolor: PRIMARY, color: 'white',
        }}>
          <Add />
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, lineHeight: 1.2 }}>
              Add Product
            </Typography>
            <Typography sx={{ fontSize: '0.78rem', opacity: 0.9 }}>
              Create a new entry in the procurement store
            </Typography>
          </Box>
          <IconButton onClick={handleCloseAddDialog} sx={{ color: 'white' }} size="small" disabled={addingProduct}>
            <Box component="span" sx={{ fontSize: 18, lineHeight: 1 }}>✕</Box>
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 2.5, bgcolor: '#f8fafc' }}>
          <Stack spacing={2}>
            {/* Section: Basic info */}
            <Paper elevation={0} sx={{ p: 1.75, borderRadius: 2, border: `1px solid ${BORDER}`, bgcolor: '#fff' }}>
              <Typography sx={{ ...LABEL_SX, mb: 1 }}>Basic Information</Typography>
              <Grid container spacing={1.25} columns={{ xs: 4, sm: 8, md: 12 }}>
                <Grid item xs={12} md={6} size={6}>
                  <TextField fullWidth size="small" label="Product Name *"
                    value={newProduct.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    disabled={addingProduct} />
                </Grid>
                <Grid item xs={12} md={6} size={6}>
                  <TextField fullWidth size="small" label="Product Code"
                    placeholder="e.g. MED-001"
                    value={newProduct.code}
                    onChange={(e) => handleInputChange('code', e.target.value)}
                    disabled={addingProduct} />
                </Grid>
                <Grid item xs={12} md={6} size={6}>
                  <FormControl fullWidth size="small" required disabled={addingProduct}>
                    <InputLabel>Product Type</InputLabel>
                    <Select
                      value={newProduct.productType}
                      onChange={(e) => handleInputChange('productType', e.target.value)}
                      label="Product Type"
                    >
                      <MenuItem value="drug">
                        <Stack direction="row" spacing={1} alignItems="center">
                          <LocalPharmacy sx={{ fontSize: 16, color: '#0369A1' }} />
                          <span>Drug</span>
                        </Stack>
                      </MenuItem>
                      {/* Consumable option removed — register consumables
                          from Procurement → Consumables instead. */}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6} size={6}>
                  <TextField fullWidth size="small" label="Dose Unit *"
                    placeholder="e.g. 500 mg, 10 ml"
                    value={newProduct.doseUnit}
                    onChange={(e) => handleInputChange('doseUnit', e.target.value)}
                    disabled={addingProduct}
                    helperText='Include a space between value and unit (e.g. "2 mg", "500 mL")'
                  />
                </Grid>
              </Grid>
            </Paper>

            {/* Section: Pricing + stock */}
            <Paper elevation={0} sx={{ p: 1.75, borderRadius: 2, border: `1px solid ${BORDER}`, bgcolor: '#fff' }}>
              <Typography sx={{ ...LABEL_SX, mb: 1 }}>Pricing & Stock</Typography>
              <Grid container spacing={1.25} columns={{ xs: 4, sm: 8, md: 12 }}>
                <Grid item xs={12} md={4} size={4}>
                  <TextField fullWidth size="small" label="Purchase Cost *"
                    type="number"
                    value={newProduct.purchaseCost}
                    onChange={(e) => handleInputChange('purchaseCost', e.target.value)}
                    disabled={addingProduct}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Typography sx={{ fontWeight: 700, color: MUTED }}>₵</Typography>
                        </InputAdornment>
                      ),
                    }} />
                </Grid>
                <Grid item xs={12} md={4} size={4}>
                  <TextField fullWidth size="small" label="Unit of Pricing *"
                    placeholder="e.g. per tablet, per box"
                    value={newProduct.unitOfPricing}
                    onChange={(e) => handleInputChange('unitOfPricing', e.target.value)}
                    disabled={addingProduct} />
                </Grid>
                <Grid item xs={12} md={4} size={4}>
                  <TextField fullWidth size="small" label="Reorder Level *"
                    type="number"
                    value={newProduct.reorderLevel}
                    onChange={(e) => handleInputChange('reorderLevel', e.target.value)}
                    disabled={addingProduct}
                    helperText="Alert when stock falls below this level" />
                </Grid>
              </Grid>
            </Paper>

            {/* Section: Insurance coverage */}
            <Paper elevation={0} sx={{ p: 1.75, borderRadius: 2, border: `1px solid ${BORDER}`, bgcolor: '#fff' }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Box>
                  <Typography sx={LABEL_SX}>Insurance Coverage</Typography>
                  <Typography sx={{ fontSize: '0.74rem', color: MUTED, mt: 0.25 }}>
                    Select every insurance scheme that should cover this product.
                  </Typography>
                </Box>
                <Stack direction="row" spacing={0.5}>
                  <Button size="small" disabled={addingProduct}
                    onClick={() => setNewProduct(prev => ({ ...prev, selectedInsurances: insuranceList.map(i => i.id) }))}
                    sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.72rem', color: PRIMARY }}>
                    Select all
                  </Button>
                  <Button size="small" disabled={addingProduct}
                    onClick={() => setNewProduct(prev => ({ ...prev, selectedInsurances: [] }))}
                    sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.72rem', color: MUTED }}>
                    Clear
                  </Button>
                </Stack>
              </Stack>

              {insuranceList.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 2, color: MUTED }}>
                  <CircularProgress size={20} sx={{ color: PRIMARY }} />
                  <Typography sx={{ fontSize: '0.78rem', mt: 0.5 }}>
                    Loading insurance providers…
                  </Typography>
                </Box>
              ) : (
                <Box sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)' },
                  gap: 0.75,
                }}>
                  {insuranceList.map((insurance) => {
                    const checked = newProduct.selectedInsurances.includes(insurance.id);
                    return (
                      <Box key={insurance.id}
                        onClick={() => !addingProduct && handleInsuranceToggle(insurance.id)}
                        sx={{
                          p: 1, borderRadius: 1.5, cursor: 'pointer',
                          border: '1px solid', borderColor: checked ? PRIMARY : BORDER,
                          bgcolor: checked ? PRIMARY_BG : '#fff',
                          display: 'flex', alignItems: 'center', gap: 0.75,
                          transition: 'all .12s ease',
                          '&:hover': { borderColor: PRIMARY },
                        }}>
                        <Checkbox size="small" checked={checked} disabled={addingProduct}
                          sx={{ p: 0.5, color: PRIMARY, '&.Mui-checked': { color: PRIMARY } }} />
                        <Typography sx={{
                          fontSize: '0.82rem',
                          fontWeight: checked ? 700 : 500,
                          color: checked ? PRIMARY_DARK : STRONG,
                        }}>
                          {insurance.name}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              )}

              {newProduct.selectedInsurances.length === 0 ? (
                <Typography sx={{ fontSize: '0.74rem', color: '#991B1B', mt: 1 }}>
                  Pick at least one insurance scheme.
                </Typography>
              ) : (
                <Typography sx={{ fontSize: '0.74rem', color: PRIMARY, mt: 1, fontWeight: 700 }}>
                  ✓ {newProduct.selectedInsurances.length} scheme{newProduct.selectedInsurances.length > 1 ? 's' : ''} selected
                </Typography>
              )}
            </Paper>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 2.5, py: 1.5, bgcolor: '#fff', borderTop: `1px solid ${BORDER}` }}>
          <Button onClick={handleCloseAddDialog} disabled={addingProduct}
            sx={{ color: MUTED, textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddProduct}
            disabled={addingProduct || !newProduct.name || !newProduct.unitOfPricing || !newProduct.purchaseCost || !newProduct.doseUnit || !newProduct.productType || !newProduct.reorderLevel || newProduct.selectedInsurances.length === 0}
            startIcon={addingProduct ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <Add />}
            sx={{
              bgcolor: PRIMARY, '&:hover': { bgcolor: PRIMARY_DARK },
              textTransform: 'none', fontWeight: 700, px: 3,
            }}
          >
            {addingProduct ? 'Adding…' : 'Add Product'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Edit Product Dialog ──────────────────────────────────────── */}
      <Dialog open={openEditDialog} onClose={handleCloseEditDialog} maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{
          display: 'flex', alignItems: 'center', gap: 1.5, py: 2,
          bgcolor: PRIMARY, color: 'white',
        }}>
          <Edit />
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, lineHeight: 1.2 }}>
              Edit Product
            </Typography>
            <Typography sx={{ fontSize: '0.78rem', opacity: 0.9 }}>
              {editingProduct?.name || 'Update product details'}
            </Typography>
          </Box>
          <IconButton onClick={handleCloseEditDialog} sx={{ color: 'white' }} size="small" disabled={updatingProduct}>
            <Box component="span" sx={{ fontSize: 18, lineHeight: 1 }}>✕</Box>
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 2.5, bgcolor: '#f8fafc' }}>
          {loadingProductDetails ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <CircularProgress size={28} sx={{ color: PRIMARY }} />
              <Typography sx={{ fontSize: '0.92rem', color: STRONG, fontWeight: 700, mt: 1.5 }}>
                Loading product details…
              </Typography>
              <Typography sx={{ fontSize: '0.78rem', color: MUTED, mt: 0.25 }}>
                Please wait while we fetch the product information
              </Typography>
            </Box>
          ) : (
            <Stack spacing={2}>
              {/* Section: Basic info */}
              <Paper elevation={0} sx={{ p: 1.75, borderRadius: 2, border: `1px solid ${BORDER}`, bgcolor: '#fff' }}>
                <Typography sx={{ ...LABEL_SX, mb: 1 }}>Basic Information</Typography>
                <Grid container spacing={1.25} columns={{ xs: 4, sm: 8, md: 12 }}>
                  <Grid item xs={12} md={6} size={6}>
                    <TextField fullWidth size="small" label="Product Name *"
                      value={editingProduct.name}
                      onChange={(e) => handleEditInputChange('name', e.target.value)}
                      disabled={updatingProduct} />
                  </Grid>
                  <Grid item xs={12} md={6} size={6}>
                    <TextField fullWidth size="small" label="Product Code"
                      placeholder="e.g. MED-001"
                      value={editingProduct.code}
                      onChange={(e) => handleEditInputChange('code', e.target.value)}
                      disabled={updatingProduct} />
                  </Grid>
                  <Grid item xs={12} md={6} size={6}>
                    <FormControl fullWidth size="small" required disabled={updatingProduct}>
                      <InputLabel>Product Type</InputLabel>
                      <Select
                        value={editingProduct.productType}
                        onChange={(e) => handleEditInputChange('productType', e.target.value)}
                        label="Product Type"
                      >
                        <MenuItem value="drug">
                          <Stack direction="row" spacing={1} alignItems="center">
                            <LocalPharmacy sx={{ fontSize: 16, color: '#0369A1' }} />
                            <span>Drug</span>
                          </Stack>
                        </MenuItem>
                        {/* Consumable option removed — managed in
                            Procurement → Consumables. */}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6} size={6}>
                    <TextField fullWidth size="small" label="Dose Unit *"
                      placeholder="e.g. 500 mg, 10 ml"
                      value={editingProduct.doseUnit}
                      onChange={(e) => handleEditInputChange('doseUnit', e.target.value)}
                      disabled={updatingProduct}
                      helperText='Include a space between value and unit (e.g. "2 mg", "500 mL")'
                    />
                  </Grid>
                </Grid>
              </Paper>

              {/* Section: Pricing + stock */}
              <Paper elevation={0} sx={{ p: 1.75, borderRadius: 2, border: `1px solid ${BORDER}`, bgcolor: '#fff' }}>
                <Typography sx={{ ...LABEL_SX, mb: 1 }}>Pricing & Stock</Typography>
                <Grid container spacing={1.25} columns={{ xs: 4, sm: 8, md: 12 }}>
                  <Grid item xs={12} md={4} size={4}>
                    <TextField fullWidth size="small" label="Purchase Cost *"
                      type="number"
                      value={editingProduct.purchaseCost}
                      onChange={(e) => handleEditInputChange('purchaseCost', e.target.value)}
                      disabled={updatingProduct}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Typography sx={{ fontWeight: 700, color: MUTED }}>₵</Typography>
                          </InputAdornment>
                        ),
                      }} />
                  </Grid>
                  <Grid item xs={12} md={4} size={4}>
                    <TextField fullWidth size="small" label="Unit of Pricing *"
                      placeholder="e.g. per tablet, per box"
                      value={editingProduct.unitOfPricing}
                      onChange={(e) => handleEditInputChange('unitOfPricing', e.target.value)}
                      disabled={updatingProduct} />
                  </Grid>
                  <Grid item xs={12} md={4} size={4}>
                    <TextField fullWidth size="small" label="Reorder Level *"
                      type="number"
                      value={editingProduct.reorderLevel}
                      onChange={(e) => handleEditInputChange('reorderLevel', e.target.value)}
                      disabled={updatingProduct}
                      helperText="Alert when stock falls below this level" />
                  </Grid>
                </Grid>
              </Paper>

              {/* Section: Insurance coverage */}
              <Paper elevation={0} sx={{ p: 1.75, borderRadius: 2, border: `1px solid ${BORDER}`, bgcolor: '#fff' }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Box>
                    <Typography sx={LABEL_SX}>Insurance Coverage</Typography>
                    <Typography sx={{ fontSize: '0.74rem', color: MUTED, mt: 0.25 }}>
                      Select every insurance scheme that should cover this product.
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={0.5}>
                    <Button size="small" disabled={updatingProduct}
                      onClick={() => setEditingProduct(prev => ({ ...prev, selectedInsurances: insuranceList.map(i => i.id) }))}
                      sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.72rem', color: PRIMARY }}>
                      Select all
                    </Button>
                    <Button size="small" disabled={updatingProduct}
                      onClick={() => setEditingProduct(prev => ({ ...prev, selectedInsurances: [] }))}
                      sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.72rem', color: MUTED }}>
                      Clear
                    </Button>
                  </Stack>
                </Stack>

                {insuranceList.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 2, color: MUTED }}>
                    <CircularProgress size={20} sx={{ color: PRIMARY }} />
                    <Typography sx={{ fontSize: '0.78rem', mt: 0.5 }}>
                      Loading insurance providers…
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)' },
                    gap: 0.75,
                  }}>
                    {insuranceList.map((insurance) => {
                      const checked = editingProduct.selectedInsurances.includes(insurance.id);
                      return (
                        <Box key={insurance.id}
                          onClick={() => !updatingProduct && handleEditInsuranceToggle(insurance.id, insurance.name)}
                          sx={{
                            p: 1, borderRadius: 1.5, cursor: 'pointer',
                            border: '1px solid', borderColor: checked ? PRIMARY : BORDER,
                            bgcolor: checked ? PRIMARY_BG : '#fff',
                            display: 'flex', alignItems: 'center', gap: 0.75,
                            transition: 'all .12s ease',
                            '&:hover': { borderColor: PRIMARY },
                          }}>
                          <Checkbox size="small" checked={checked} disabled={updatingProduct}
                            sx={{ p: 0.5, color: PRIMARY, '&.Mui-checked': { color: PRIMARY } }} />
                          <Typography sx={{
                            fontSize: '0.82rem',
                            fontWeight: checked ? 700 : 500,
                            color: checked ? PRIMARY_DARK : STRONG,
                          }}>
                            {insurance.name}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                )}

                {editingProduct.selectedInsurances.length === 0 ? (
                  <Typography sx={{ fontSize: '0.74rem', color: '#991B1B', mt: 1 }}>
                    Pick at least one insurance scheme.
                  </Typography>
                ) : (
                  <Typography sx={{ fontSize: '0.74rem', color: PRIMARY, mt: 1, fontWeight: 700 }}>
                    ✓ {editingProduct.selectedInsurances.length} scheme{editingProduct.selectedInsurances.length > 1 ? 's' : ''} selected
                  </Typography>
                )}
              </Paper>
            </Stack>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 2.5, py: 1.5, bgcolor: '#fff', borderTop: `1px solid ${BORDER}` }}>
          <Button onClick={handleCloseEditDialog} disabled={updatingProduct}
            sx={{ color: MUTED, textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleUpdateProduct}
            disabled={updatingProduct || loadingProductDetails || !editingProduct.name || !editingProduct.unitOfPricing || !editingProduct.purchaseCost || !editingProduct.doseUnit || !editingProduct.productType || !editingProduct.reorderLevel || editingProduct.selectedInsurances.length === 0}
            startIcon={updatingProduct ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <Edit />}
            sx={{
              bgcolor: PRIMARY, '&:hover': { bgcolor: PRIMARY_DARK },
              textTransform: 'none', fontWeight: 700, px: 3,
            }}
          >
            {updatingProduct ? 'Updating…' : 'Update Product'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Stock Adjustment Dialog ──────────────────────────────────── */}
      <Dialog open={openMovementDialog} onClose={handleCloseMovementDialog} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{
          display: 'flex', alignItems: 'center', gap: 1.5, py: 2,
          bgcolor: PRIMARY, color: 'white',
        }}>
          <SwapHoriz />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, lineHeight: 1.2 }}>
                Adjust Stock
              </Typography>
              <Chip
                size="small"
                icon={<LockOutlined sx={{ fontSize: 12, color: 'rgba(255,255,255,0.85) !important' }} />}
                label="Admin only"
                sx={{
                  height: 18, fontSize: '0.62rem', fontWeight: 700,
                  bgcolor: 'rgba(255,255,255,0.18)', color: 'white',
                }}
              />
            </Stack>
            <Typography noWrap sx={{ fontSize: '0.78rem', opacity: 0.9 }}>
              {selectedProduct?.name || ''}
            </Typography>
          </Box>
          <IconButton onClick={handleCloseMovementDialog} sx={{ color: 'white' }} size="small" disabled={adjusting}>
            <Box component="span" sx={{ fontSize: 18, lineHeight: 1 }}>✕</Box>
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 2.5, bgcolor: '#f8fafc' }}>
          {/* Current stock — three slim outlined tiles */}
          {selectedProduct && (
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 1, mb: 2,
            }}>
              {[
                { label: 'Store',      value: parseInt(selectedProduct.inventory_stock  || 0), tint: 'rgba(234,88,12,0.10)',  color: '#A0531A' },
                { label: 'Dispensary', value: parseInt(selectedProduct.dispensary_stock || 0), tint: 'rgba(2,132,199,0.10)',  color: '#0369A1' },
                { label: 'Total',      value: parseInt(selectedProduct.current_stock    || 0), tint: PRIMARY_BG,              color: PRIMARY_DARK },
              ].map(({ label, value, tint, color }) => (
                <Paper key={label} elevation={0} sx={{
                  p: 1.25, borderRadius: 1.5,
                  border: `1px solid ${BORDER}`, bgcolor: '#fff',
                  textAlign: 'center',
                }}>
                  <Typography sx={LABEL_SX}>{label}</Typography>
                  <Box sx={{
                    display: 'inline-block', mt: 0.5,
                    px: 1, py: 0.25, borderRadius: 1,
                    bgcolor: tint, color,
                  }}>
                    <Typography sx={{ fontSize: '1.05rem', fontWeight: 800, lineHeight: 1.1 }}>
                      {value.toLocaleString()}
                    </Typography>
                  </Box>
                </Paper>
              ))}
            </Box>
          )}

          {/* Adjustment Type */}
          <Typography sx={{ ...LABEL_SX, mb: 0.75 }}>Adjustment Type</Typography>
          <Box sx={{
            display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 1.75,
          }}>
            {[
              { value: 'add',      label: 'Add Stock',    icon: <AddCircleOutline    sx={{ fontSize: 18 }} />, color: '#15803D', tint: 'rgba(22,163,74,0.10)' },
              { value: 'subtract', label: 'Remove Stock', icon: <RemoveCircleOutline sx={{ fontSize: 18 }} />, color: '#991B1B', tint: 'rgba(220,38,38,0.10)' },
            ].map(({ value, label, icon, color, tint }) => {
              const active = adjustmentType === value;
              return (
                <Box key={value} role="button"
                  onClick={() => !adjusting && setAdjustmentType(value)}
                  sx={{
                    p: 1.25, borderRadius: 1.5,
                    border: '1px solid', borderColor: active ? color : BORDER,
                    bgcolor: active ? tint : '#fff',
                    cursor: adjusting ? 'not-allowed' : 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75,
                    color: active ? color : MUTED,
                    transition: 'all .12s ease',
                    '&:hover': { borderColor: color },
                  }}>
                  {icon}
                  <Typography sx={{ fontSize: '0.84rem', fontWeight: 700 }}>
                    {label}
                  </Typography>
                </Box>
              );
            })}
          </Box>

          {/* Target Location */}
          <Typography sx={{ ...LABEL_SX, mb: 0.75 }}>Location</Typography>
          <Box sx={{
            display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 1.75,
          }}>
            {[
              { value: 'store',      label: 'Store',      icon: <Store         sx={{ fontSize: 18 }} />, color: '#A0531A', tint: 'rgba(234,88,12,0.10)' },
              { value: 'dispensary', label: 'Dispensary', icon: <LocalHospital sx={{ fontSize: 18 }} />, color: '#0369A1', tint: 'rgba(2,132,199,0.10)' },
            ].map(({ value, label, icon, color, tint }) => {
              const active = adjustmentTarget === value;
              return (
                <Box key={value} role="button"
                  onClick={() => !adjusting && setAdjustmentTarget(value)}
                  sx={{
                    p: 1.25, borderRadius: 1.5,
                    border: '1px solid', borderColor: active ? color : BORDER,
                    bgcolor: active ? tint : '#fff',
                    cursor: adjusting ? 'not-allowed' : 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75,
                    color: active ? color : MUTED,
                    transition: 'all .12s ease',
                    '&:hover': { borderColor: color },
                  }}>
                  {icon}
                  <Typography sx={{ fontSize: '0.84rem', fontWeight: 700 }}>
                    {label}
                  </Typography>
                </Box>
              );
            })}
          </Box>

          {/* Quantity */}
          <TextField
            fullWidth size="small"
            label="Quantity *"
            type="number"
            value={adjustmentQty}
            onChange={(e) => setAdjustmentQty(e.target.value)}
            inputProps={{ min: 1 }}
            disabled={adjusting}
            sx={{ mb: 1.5 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  {adjustmentType === 'add'
                    ? <AddCircleOutline    sx={{ fontSize: 16, color: '#15803D' }} />
                    : <RemoveCircleOutline sx={{ fontSize: 16, color: '#991B1B' }} />}
                </InputAdornment>
              ),
            }}
          />

          {/* Reason */}
          <TextField
            fullWidth size="small"
            label="Reason for Adjustment *"
            multiline rows={2}
            value={adjustmentReason}
            onChange={(e) => setAdjustmentReason(e.target.value)}
            placeholder="e.g. damaged stock removed, stock count correction, transfer from warehouse"
            disabled={adjusting}
          />
        </DialogContent>

        <DialogActions sx={{ px: 2.5, py: 1.5, bgcolor: '#fff', borderTop: `1px solid ${BORDER}` }}>
          <Button onClick={handleCloseMovementDialog} disabled={adjusting}
            sx={{ color: MUTED, textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAdjustStock}
            disabled={!adjustmentQty || !adjustmentReason.trim() || adjusting}
            startIcon={adjusting ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <CheckCircle />}
            sx={{
              bgcolor: PRIMARY, '&:hover': { bgcolor: PRIMARY_DARK },
              textTransform: 'none', fontWeight: 700, px: 3,
            }}
          >
            {adjusting ? 'Processing…' : 'Apply Adjustment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Password Confirmation Dialog ─────────────────────────────── */}
      <Dialog open={openPwdDialog} onClose={() => !adjusting && setOpenPwdDialog(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{
          display: 'flex', alignItems: 'center', gap: 1.5, py: 2,
          bgcolor: PRIMARY, color: 'white',
        }}>
          <LockOutlined fontSize="small" />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: '1rem', fontWeight: 700, lineHeight: 1.2 }}>
              Confirm Adjustment
            </Typography>
            <Typography noWrap sx={{ fontSize: '0.74rem', opacity: 0.9 }}>
              {selectedProduct?.name || ''}
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 2.25, bgcolor: '#f8fafc' }}>
          {pendingAdjustment && (
            <Paper elevation={0} sx={{
              p: 1.25, mb: 1.5, borderRadius: 1.5,
              border: `1px solid ${BORDER}`, bgcolor: '#fff',
            }}>
              <Typography sx={{ fontSize: '0.82rem', color: STRONG }}>
                <Box component="span" sx={{ color: pendingAdjustment.type === 'add' ? '#15803D' : '#991B1B', fontWeight: 800 }}>
                  {pendingAdjustment.type === 'add' ? 'Add' : 'Remove'}
                </Box>{' '}
                <strong>{pendingAdjustment.qty}</strong> units ·{' '}
                <strong>{pendingAdjustment.target === 'store' ? 'Store' : 'Dispensary'}</strong>
              </Typography>
            </Paper>
          )}
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1.5 }}>
            <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: STRONG }}>
              {JSON.parse(localStorage.getItem('userInfo') || '{}').username || 'Admin'}
            </Typography>
            <Chip
              size="small" label="System Administrator"
              sx={{
                height: 18, fontSize: '0.62rem', fontWeight: 700,
                bgcolor: PRIMARY_BG, color: PRIMARY_DARK,
              }}
            />
          </Stack>
          <TextField
            fullWidth size="small"
            label="Password"
            type="password"
            value={pwdValue}
            onChange={(e) => { setPwdValue(e.target.value); setPwdError(''); }}
            onKeyDown={(e) => { if (e.key === 'Enter') handleConfirmAdjustment(); }}
            error={!!pwdError}
            helperText={pwdError}
            autoFocus
            disabled={adjusting}
            autoComplete="current-password"
          />
        </DialogContent>

        <DialogActions sx={{ px: 2.25, py: 1.5, bgcolor: '#fff', borderTop: `1px solid ${BORDER}` }}>
          <Button onClick={() => setOpenPwdDialog(false)} disabled={adjusting}
            sx={{ color: MUTED, textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmAdjustment}
            disabled={adjusting}
            startIcon={adjusting ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <CheckCircle />}
            sx={{
              bgcolor: PRIMARY, '&:hover': { bgcolor: PRIMARY_DARK },
              textTransform: 'none', fontWeight: 700, px: 3,
            }}
          >
            {adjusting ? 'Processing…' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete Product Dialog (per-insurance OR delete all) ─────────── */}
      <Dialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{
          display: 'flex', alignItems: 'center', gap: 1.5, py: 2,
          bgcolor: '#dc2626', color: 'white',
        }}>
          <DeleteOutline />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, lineHeight: 1.2 }}>
              Delete Product
            </Typography>
            <Typography noWrap sx={{ fontSize: '0.78rem', opacity: 0.9 }}>
              {deleteDialogProduct?.name || ''}
            </Typography>
          </Box>
          <IconButton
            onClick={handleCloseDeleteDialog}
            sx={{ color: 'white' }} size="small"
            disabled={deleteSubmitting}
          >
            <Box component="span" sx={{ fontSize: 18, lineHeight: 1 }}>✕</Box>
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 2.5, bgcolor: '#f8fafc' }}>
          {/* Bold warning banner */}
          <Paper
            elevation={0}
            sx={{
              p: 1.5, mb: 2, borderRadius: 2,
              border: '1px solid #fecaca', bgcolor: '#FEF2F2',
              display: 'flex', alignItems: 'flex-start', gap: 1.25,
            }}
          >
            <Shield sx={{ color: '#dc2626', fontSize: 22, mt: '2px' }} />
            <Box>
              <Typography sx={{ fontSize: '0.95rem', fontWeight: 800, color: '#991B1B', lineHeight: 1.25 }}>
                Choose how you want to delete this medication
              </Typography>
              <Typography sx={{ fontSize: '0.8rem', color: '#7f1d1d', mt: 0.5 }}>
                Pick <strong>“Delete from a specific insurance”</strong> to detach this
                product from one scheme only — its inventory stays intact.
                Pick <strong>“Delete entire product”</strong> to wipe it from the system —
                store, dispensary, and every insurance — regardless of remaining stock.
              </Typography>
            </Box>
          </Paper>

          {/* Mode selector */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} sx={{ mb: 2 }}>
            {[
              {
                value: 'specific',
                title: 'Delete from a specific insurance',
                subtitle: 'Detach from one scheme',
                color: '#0ea5e9',
                bg: 'rgba(14,165,233,0.08)',
                icon: <Delete sx={{ fontSize: 18 }} />,
              },
              {
                value: 'all',
                title: 'Delete entire product',
                subtitle: 'Wipe from store + dispensary + all insurances',
                color: '#dc2626',
                bg: 'rgba(220,38,38,0.08)',
                icon: <DeleteSweep sx={{ fontSize: 18 }} />,
              },
            ].map((opt) => {
              const active = deleteMode === opt.value;
              return (
                <Paper
                  key={opt.value}
                  elevation={0}
                  onClick={() => !deleteSubmitting && setDeleteMode(opt.value)}
                  sx={{
                    flex: 1, p: 1.25, borderRadius: 2, cursor: deleteSubmitting ? 'not-allowed' : 'pointer',
                    border: `2px solid ${active ? opt.color : BORDER}`,
                    bgcolor: active ? opt.bg : '#fff',
                    transition: 'all 0.15s ease',
                    '&:hover': !deleteSubmitting && { borderColor: opt.color, bgcolor: opt.bg },
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Box sx={{
                      width: 28, height: 28, borderRadius: '50%',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      bgcolor: active ? opt.color : '#f1f5f9', color: active ? '#fff' : MUTED,
                    }}>
                      {opt.icon}
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: active ? opt.color : STRONG, lineHeight: 1.15 }}>
                        {opt.title}
                      </Typography>
                      <Typography sx={{ fontSize: '0.7rem', color: MUTED }}>
                        {opt.subtitle}
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
              );
            })}
          </Stack>

          {/* Per-insurance picker */}
          {deleteMode === 'specific' && (
            <Paper elevation={0} sx={{
              p: 1.5, borderRadius: 2,
              border: `1px solid ${BORDER}`, bgcolor: '#fff',
            }}>
              <Typography sx={{ ...LABEL_SX, mb: 1 }}>Pick insurance to remove</Typography>
              {deleteDialogLoading ? (
                <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 1.5 }}>
                  <CircularProgress size={16} sx={{ color: PRIMARY }} />
                  <Typography sx={{ fontSize: '0.82rem', color: MUTED }}>
                    Loading insurance associations…
                  </Typography>
                </Stack>
              ) : deleteDialogInsurances.length === 0 ? (
                <Alert severity="info" sx={{ fontSize: '0.8rem' }}>
                  This product is not currently linked to any insurance.
                  Use <strong>Delete entire product</strong> to remove it from the system.
                </Alert>
              ) : (
                <FormControl fullWidth size="small">
                  <InputLabel id="delete-insurance-label">Insurance</InputLabel>
                  <Select
                    labelId="delete-insurance-label"
                    label="Insurance"
                    value={deleteSelectedInsurance}
                    onChange={(e) => setDeleteSelectedInsurance(e.target.value)}
                    disabled={deleteSubmitting}
                  >
                    {deleteDialogInsurances.map((ins) => (
                      <MenuItem key={ins.id} value={String(ins.id)}>
                        {ins.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Paper>
          )}

          {/* Delete-all confirmation block */}
          {deleteMode === 'all' && (
            <Paper elevation={0} sx={{
              p: 1.5, borderRadius: 2,
              border: '1px solid #fecaca', bgcolor: '#fff',
            }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <Typography sx={{ fontSize: '0.85rem', fontWeight: 800, color: '#991B1B' }}>
                  This action cannot be undone
                </Typography>
                <Chip
                  size="small" label="System Administrator"
                  sx={{
                    height: 18, fontSize: '0.62rem', fontWeight: 700,
                    bgcolor: 'rgba(220,38,38,0.10)', color: '#991B1B',
                  }}
                />
              </Stack>
              <Typography sx={{ fontSize: '0.8rem', color: STRONG, mb: 1.5 }}>
                Removes <strong>{deleteDialogProduct?.name}</strong> from{' '}
                <code style={{ color: '#dc2626' }}>store_pro</code>,{' '}
                <code style={{ color: '#dc2626' }}>inventory_bal</code>, and{' '}
                <code style={{ color: '#dc2626' }}>dispensary_bal</code> across every insurance.
                {Number(deleteDialogProduct?.current_stock || 0) > 0 && (
                  <> Any remaining stock ({deleteDialogProduct?.current_stock} unit
                  {Number(deleteDialogProduct?.current_stock || 0) === 1 ? '' : 's'}) will be discarded.</>
                )}
              </Typography>
              <TextField
                fullWidth size="small"
                label="Confirm with your password"
                type="password"
                value={deleteAllPwd}
                onChange={(e) => { setDeleteAllPwd(e.target.value); setDeleteAllPwdError(''); }}
                onKeyDown={(e) => { if (e.key === 'Enter') handleSubmitDelete(); }}
                error={!!deleteAllPwdError}
                helperText={deleteAllPwdError}
                disabled={deleteSubmitting}
                autoComplete="current-password"
              />
            </Paper>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 2.25, py: 1.5, bgcolor: '#fff', borderTop: `1px solid ${BORDER}` }}>
          <Button
            onClick={handleCloseDeleteDialog}
            disabled={deleteSubmitting}
            sx={{ color: MUTED, textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmitDelete}
            disabled={
              deleteSubmitting ||
              deleteDialogLoading ||
              (deleteMode === 'specific' && (!deleteSelectedInsurance || deleteDialogInsurances.length === 0)) ||
              (deleteMode === 'all' && !deleteAllPwd.trim())
            }
            startIcon={
              deleteSubmitting
                ? <CircularProgress size={16} sx={{ color: 'white' }} />
                : deleteMode === 'all' ? <DeleteSweep /> : <Delete />
            }
            sx={{
              bgcolor: '#dc2626', '&:hover': { bgcolor: '#b91c1c' },
              textTransform: 'none', fontWeight: 700, px: 3,
            }}
          >
            {deleteSubmitting
              ? (deleteMode === 'all' ? 'Deleting…' : 'Removing…')
              : (deleteMode === 'all' ? 'Delete Entire Product' : 'Remove from Insurance')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete Product — password confirmation ──────────────────────── */}
      <Dialog open={deletePwdOpen} onClose={() => !deleting && setDeletePwdOpen(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{
          display: 'flex', alignItems: 'center', gap: 1.5, py: 2,
          bgcolor: '#dc2626', color: 'white',
        }}>
          <DeleteOutline fontSize="small" />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: '1rem', fontWeight: 700, lineHeight: 1.2 }}>
              Confirm Delete
            </Typography>
            <Typography noWrap sx={{ fontSize: '0.74rem', opacity: 0.9 }}>
              {deleteTargetProduct?.name || ''}
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 2.25, bgcolor: '#f8fafc' }}>
          <Paper elevation={0} sx={{
            p: 1.25, mb: 1.5, borderRadius: 1.5,
            border: `1px solid ${BORDER}`, bgcolor: '#fff',
          }}>
            <Typography sx={{ fontSize: '0.82rem', color: STRONG }}>
              Removes <strong>{deleteTargetProduct?.name || ''}</strong> from{' '}
              <code style={{ color: '#dc2626' }}>store_pro</code>,{' '}
              <code style={{ color: '#dc2626' }}>inventory_bal</code>, and{' '}
              <code style={{ color: '#dc2626' }}>dispensary_bal</code>.
            </Typography>
            <Typography sx={{ fontSize: '0.74rem', color: '#991B1B', mt: 0.5, fontWeight: 700 }}>
              This action cannot be undone.
            </Typography>
          </Paper>
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1.5 }}>
            <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: STRONG }}>
              {JSON.parse(localStorage.getItem('userInfo') || '{}').username || 'Admin'}
            </Typography>
            <Chip
              size="small" label="System Administrator"
              sx={{
                height: 18, fontSize: '0.62rem', fontWeight: 700,
                bgcolor: 'rgba(220,38,38,0.10)', color: '#991B1B',
              }}
            />
          </Stack>
          <TextField
            fullWidth size="small"
            label="Password"
            type="password"
            value={deletePwdValue}
            onChange={(e) => { setDeletePwdValue(e.target.value); setDeletePwdError(''); }}
            onKeyDown={(e) => { if (e.key === 'Enter') handleConfirmDeleteProduct(); }}
            error={!!deletePwdError}
            helperText={deletePwdError}
            autoFocus
            disabled={deleting}
            autoComplete="current-password"
          />
        </DialogContent>

        <DialogActions sx={{ px: 2.25, py: 1.5, bgcolor: '#fff', borderTop: `1px solid ${BORDER}` }}>
          <Button onClick={() => setDeletePwdOpen(false)} disabled={deleting}
            sx={{ color: MUTED, textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmDeleteProduct}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <DeleteOutline />}
            sx={{
              bgcolor: '#dc2626', '&:hover': { bgcolor: '#b91c1c' },
              textTransform: 'none', fontWeight: 700, px: 3,
            }}
          >
            {deleting ? 'Deleting…' : 'Delete Product'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Loading Backdrop */}
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1, backdropFilter: 'blur(4px)', bgcolor: 'rgba(0, 0, 0, 0.7)' }} open={loadingProducts}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} sx={{ color: '#0ea5e9' }} />
          <Typography variant="h6" sx={{ mt: 3, fontWeight: 500 }}>Loading products...</Typography>
          <Typography variant="body2" sx={{ mt: 1, opacity: 0.8 }}>Please wait while we fetch your inventory</Typography>
        </Box>
      </Backdrop>
    </Box>
  );
};

export default ProcurementHome;